import { Suspense } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Bell, Briefcase, Home, MoreHorizontal, TrendingUp, BookOpen } from 'react-feather';
import { PageVisitToast } from '@/core/error/PageVisitToast';
import { useAuth } from '@/core/auth/useAuth';
import { isNotificationUnread } from '@/features/notifications/api';
import { useNotifications } from '@/features/notifications/hooks';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import './StudentAppLayout.scss';

const NAV = [
  { to: '/student/academics', label: 'Academics', icon: BookOpen, end: false },
  { to: '/student/career', label: 'Career', icon: TrendingUp, end: false },
  { to: '/student', label: 'Home', icon: Home, end: true, center: true },
  { to: '/student/jobs', label: 'Jobs', icon: Briefcase, end: false },
  { to: '/student/more', label: 'More', icon: MoreHorizontal, end: false },
] as const;

/**
 * Mobile-first student “app” shell.
 * Full-bleed on phones; centered phone frame on larger screens so DevTools
 * responsive mode and real devices share the same experience.
 */
export function StudentAppLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: notifications } = useNotifications();
  const alertCount = (notifications ?? []).filter(isNotificationUnread).length;

  return (
    <div className="student-app-root">
      <PageVisitToast />
      <div className="student-app">
        <header className="student-app__topbar">
          <div className="student-app__brand">
            <span className="student-app__brand-mark">R</span>
            <div>
              <div className="student-app__brand-title">RCATS</div>
              <div className="student-app__brand-sub">Student</div>
            </div>
          </div>
          <div className="student-app__topbar-actions">
            <button
              type="button"
              className="student-app__notify"
              title="Alerts"
              aria-label={
                alertCount > 0 ? `Alerts, ${alertCount} messages` : 'Alerts'
              }
              onClick={() => navigate('/student/alerts')}
            >
              <Bell size={20} strokeWidth={2} aria-hidden />
              {alertCount > 0 ? (
                <span className="student-app__notify-badge">
                  {alertCount > 9 ? '9+' : alertCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className="student-app__avatar"
              title={user?.fullName ?? 'Account'}
              aria-label="Account"
              onClick={() => navigate('/student/account')}
            >
              <UserAvatar
                fullName={user?.fullName}
                avatarUrl={user?.avatarUrl}
                size={36}
                fallback="icon"
              />
            </button>
          </div>
        </header>

        <main className="student-app__main">
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>

        <nav className="student-app__tabbar" aria-label="Student app">
          {NAV.map(({ to, label, icon: Icon, end, ...rest }) => {
            const center = 'center' in rest && rest.center;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  [
                    'student-app__tab',
                    center ? 'student-app__tab--center' : '',
                    isActive ? 'student-app__tab--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
              >
                <span className={center ? 'student-app__tab-fab' : undefined}>
                  <Icon size={center ? 22 : 20} strokeWidth={2} aria-hidden />
                </span>
                <span>{label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
